/**
 * Plazo para completar el expediente (CC-02, SP-247 en el back y SP-248 aquí).
 *
 * El back manda un objeto `profileDeadline` con el estado del plazo; aquí solo
 * se traduce a lo que ve cada quien. Vive fuera del componente para poder
 * probarlo sin montarlo.
 */

/** `running`: corre. `paused`: en revisión de Contraloría. `expired`: venció incompleto. */
export type EstadoDelPlazo = 'running' | 'paused' | 'expired'

export interface PlazoDelExpediente {
  /** YYYY-MM-DD. Null cuando el plazo está en pausa o no aplica. */
  deadlineAt: string | null
  /** Días hábiles que le quedan. En pausa trae los que le quedaban. */
  businessDaysRemaining: number | null
  /** Null: no aplica (proveedor aprobado o anterior al plazo). */
  state: EstadoDelPlazo | null
  /** Ya vienen con texto para mostrar. */
  missingFields: string[]
  missingDocuments: string[]
}

/** Quién mira: el proveedor ve su plazo; Compras/Contraloría ven el del proveedor. */
export type VistaDelPlazo = 'proveedor' | 'admin'

export type TonoDelPlazo = 'azul' | 'amarillo' | 'rojo'

export interface ResumenDelPlazo {
  tono: TonoDelPlazo
  titulo: string
  detalle: string | null
  datosFaltantes: string[]
  documentosFaltantes: string[]
}

/** Con tantos días hábiles o menos, el plazo se pinta como urgente. */
export const DIAS_PARA_AVISO_URGENTE = 2

/**
 * `YYYY-MM-DD` → `dd/mm/yyyy` partiendo la cadena.
 *
 * No pasa por `new Date()`: la fecha no trae hora, se leería como medianoche UTC
 * y en México se pintaría el día anterior.
 */
export const fechaDelPlazo = (fecha: string | null | undefined): string | null => {
  const [anio, mes, dia] = (fecha ?? '').split('-')
  if (!anio || !mes || !dia) return null
  return `${dia.slice(0, 2)}/${mes}/${anio}`
}

export const textoDeDias = (dias: number): string =>
  dias === 1 ? '1 día hábil' : `${dias} días hábiles`

/** "Te queda 1 día" pero "Te quedan 4 días": el verbo concuerda con la cuenta. */
const verboDeQuedar = (propio: boolean, dias: number, pasado: boolean): string => {
  const sujeto = propio ? 'Te' : 'Le'
  const verbo = dias === 1 ? 'queda' : 'quedan'
  return `${sujeto} ${pasado ? `${verbo.slice(0, -1)}${dias === 1 ? 'ba' : 'ban'}` : verbo}`
}

const limpiar = (lista: string[] | null | undefined): string[] =>
  (lista ?? []).map((texto) => texto.trim()).filter(Boolean)

/**
 * Lo que se le dice a cada quien sobre el plazo, o null cuando no hay nada que
 * decir (sin plazo, o un estado que esta versión no conoce).
 */
export const resumenDelPlazo = (
  plazo: PlazoDelExpediente | null | undefined,
  vista: VistaDelPlazo,
): ResumenDelPlazo | null => {
  if (!plazo?.state) return null

  const datosFaltantes = limpiar(plazo.missingFields)
  const documentosFaltantes = limpiar(plazo.missingDocuments)
  const propio = vista === 'proveedor'
  const dias = plazo.businessDaysRemaining
  const fecha = fechaDelPlazo(plazo.deadlineAt)

  switch (plazo.state) {
    case 'running': {
      const urgente = dias !== null && dias <= DIAS_PARA_AVISO_URGENTE
      const restantes =
        dias === null
          ? null
          : dias <= 0
            ? propio
              ? 'Hoy es tu último día hábil.'
              : 'Hoy es su último día hábil.'
            : `${verboDeQuedar(propio, dias, false)} ${textoDeDias(dias)}.`
      return {
        tono: urgente ? 'amarillo' : 'azul',
        titulo: fecha
          ? propio
            ? `Tienes hasta el ${fecha} para completar tu expediente`
            : `Plazo para completar el expediente: vence el ${fecha}`
          : propio
            ? 'Tienes un plazo para completar tu expediente'
            : 'El expediente tiene un plazo para completarse',
        detalle: restantes,
        datosFaltantes,
        documentosFaltantes,
      }
    }

    case 'paused': {
      const quedaban =
        dias === null
          ? ''
          : ` ${verboDeQuedar(propio, dias, true)} ${textoDeDias(Math.max(dias, 0))}.`
      return {
        tono: 'azul',
        titulo: propio ? 'Tu plazo está en pausa' : 'Plazo en pausa',
        detalle: `${
          propio ? 'Tu expediente está' : 'El expediente está'
        } en revisión de Contraloría y el plazo no corre.${quedaban}`,
        datosFaltantes,
        documentosFaltantes,
      }
    }

    case 'expired':
      return {
        tono: 'rojo',
        titulo: propio
          ? 'El plazo para completar tu expediente venció'
          : 'Plazo vencido: proveedor bloqueado',
        detalle: propio
          ? 'Tu expediente quedó incompleto y por eso hay acciones bloqueadas. Se desbloquea solo en cuanto completes lo pendiente.'
          : 'El expediente venció incompleto. Se desbloquea solo en cuanto el proveedor completa lo pendiente.',
        datosFaltantes,
        documentosFaltantes,
      }

    default:
      return null
  }
}
